/**
 * PrepMe — Scroll Animations
 * GSAP + ScrollTrigger
 *
 * Firefox bfcache fix: when the user hits Back, Firefox restores the page
 * from cache with GSAP's inline opacity:0 still applied but ScrollTrigger
 * instances gone. The pageshow listener detects this and fully reinitialises.
 */
(function () {
  'use strict';

  /* Selectors for every element we animate — needed for bfcache reset */
  var ANIM_SELECTOR =
    '.multicolumn--kako .multicolumn-list__item,' +
    '.multicolumn--kako .multicolumn__title,' +
    '.multicolumn--kljucne .multicolumn-list__item,' +
    '.multicolumn--kljucne .multicolumn__title';

  function initAnimations() {
    /* Kill any stale ScrollTrigger instances from a previous init
     * (happens on bfcache restoration or Shopify instant-page re-use). */
    ScrollTrigger.getAll().forEach(function (t) { t.kill(); });

    gsap.registerPlugin(ScrollTrigger);

    /* Respect prefers-reduced-motion — make everything visible instantly */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll(ANIM_SELECTOR).forEach(function (el) {
        gsap.set(el, { clearProps: 'all' });
      });
      return;
    }

    /* ── Strip Dawn's built-in scroll classes so they don't conflict ── */
    document
      .querySelectorAll(
        '.multicolumn--kako .scroll-trigger, .multicolumn--kako .animate--slide-in,' +
        '.multicolumn--kljucne .scroll-trigger, .multicolumn--kljucne .animate--slide-in'
      )
      .forEach(function (el) {
        el.classList.remove('scroll-trigger', 'animate--slide-in');
        el.style.opacity = '';
        el.style.transform = '';
        el.style.visibility = '';
      });

    /* ─────────────────────────────────────────────────────────────────
     * KAKO FUNKCIONIRA — dark section, dramatic stagger
     * ───────────────────────────────────────────────────────────────── */
    var kakoSection = document.querySelector('.multicolumn--kako');
    if (kakoSection) {
      var kakoItems   = kakoSection.querySelectorAll('.multicolumn-list__item');
      var kakoHeading = kakoSection.querySelector('.multicolumn__title');
      var stepBadges  = kakoSection.querySelectorAll('.multicolumn-card__step');

      if (kakoHeading) gsap.set(kakoHeading, { y: 30, opacity: 0 });
      gsap.set(kakoItems, { y: 50, opacity: 0, scale: 0.96 });

      if (kakoHeading) {
        ScrollTrigger.create({
          trigger: kakoSection,
          start: 'top 82%',
          once: true,
          onEnter: function () {
            gsap.to(kakoHeading, { y: 0, opacity: 1, duration: 0.7, ease: 'power2.out' });
          }
        });
      }

      ScrollTrigger.create({
        trigger: kakoSection,
        start: 'top 74%',
        once: true,
        onEnter: function () {
          gsap.to(kakoItems, {
            y: 0, opacity: 1, scale: 1,
            duration: 0.6, stagger: 0.12, ease: 'power3.out'
          });

          stepBadges.forEach(function (badge) {
            var target = parseInt(badge.textContent, 10);
            if (isNaN(target)) return;
            var obj = { val: 0 };
            gsap.to(obj, {
              val: target, duration: 0.5, delay: 0.3, ease: 'power2.out',
              onUpdate: function () { badge.textContent = Math.ceil(obj.val); }
            });
          });
        }
      });
    }

    /* ─────────────────────────────────────────────────────────────────
     * KLJUČNE PREDNOSTI — cards pop in with bounce
     * ───────────────────────────────────────────────────────────────── */
    var kljucneSection = document.querySelector('.multicolumn--kljucne');
    if (kljucneSection) {
      var kljucneItems   = kljucneSection.querySelectorAll('.multicolumn-list__item');
      var kljucneHeading = kljucneSection.querySelector('.multicolumn__title');

      if (kljucneHeading) gsap.set(kljucneHeading, { y: 30, opacity: 0 });
      gsap.set(kljucneItems, { y: 60, opacity: 0 });

      if (kljucneHeading) {
        ScrollTrigger.create({
          trigger: kljucneSection,
          start: 'top 82%',
          once: true,
          onEnter: function () {
            gsap.to(kljucneHeading, { y: 0, opacity: 1, duration: 0.7, ease: 'power2.out' });
          }
        });
      }

      ScrollTrigger.create({
        trigger: kljucneSection,
        start: 'top 74%',
        once: true,
        onEnter: function () {
          gsap.to(kljucneItems, {
            y: 0, opacity: 1,
            duration: 0.8, stagger: 0.16, ease: 'back.out(1.6)'
          });
        }
      });

      kljucneSection.querySelectorAll('.multicolumn-card__info').forEach(function (card, i) {
        ScrollTrigger.create({
          trigger: card,
          start: 'top 80%',
          once: true,
          onEnter: function () {
            gsap.fromTo(card, { '--after-opacity': 0 }, {
              '--after-opacity': 1,
              duration: 1, delay: 0.4 + i * 0.15, ease: 'power1.out'
            });
          }
        });
      });
    }
  }

  /* ── Initial boot ──────────────────────────────────────────────────────────
   * GSAP + ScrollTrigger are loaded with `defer`, which guarantees they run
   * before the `load` event fires. Waiting for `load` eliminates the old
   * setTimeout polling loop that caused 7,000+ ms Total Blocking Time.
   * ─────────────────────────────────────────────────────────────────────── */
  if (document.readyState === 'complete') {
    /* Page already fully loaded (e.g. script injected late) */
    initAnimations();
  } else {
    window.addEventListener('load', initAnimations, { once: true });
  }

  /* ── bfcache restoration (Firefox Back button) ──────────────────────
   * When persisted === true the page was served from Firefox's
   * Back-Forward Cache. GSAP's inline opacity:0 is still on the DOM
   * but all ScrollTrigger instances are dead. Clear inline styles and
   * reinitialise so elements are never stuck invisible.
   * ─────────────────────────────────────────────────────────────────── */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    if (typeof gsap === 'undefined') return;

    /* Wipe any GSAP inline styles left over from before navigation */
    document.querySelectorAll(ANIM_SELECTOR).forEach(function (el) {
      gsap.set(el, { clearProps: 'all' });
    });

    initAnimations();
  });

})();
